<template>
  <div class="fill-contract-container">
    <!-- 返回按钮 -->
    <div class="back-button">
      <a-button type="text" @click="goBack">
        <template #icon>
          <LeftOutlined />
        </template>
        返回首页
      </a-button>
    </div>

    <!-- 步骤条 -->
    <div class="steps-container">
      <a-steps :current="currentStep" :items="steps"></a-steps>
    </div>

    <!-- 步骤内容区域 -->
    <div class="steps-content">
      <!-- 第一步：上传文件 -->
      <div v-if="currentStep === 0" class="step-content">
        <h2>上传合同文件</h2>
        <div class="upload-section">
          <a-upload-dragger
            name="file"
            :multiple="true"
            :file-list="fileList"
            :before-upload="beforeUpload"
            @remove="handleRemove"
            :custom-request="customUpload"
          >
            <p class="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p class="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p class="ant-upload-hint">
              支持上传多个文件，请确保文件格式正确
            </p>
          </a-upload-dragger>
        </div>
        <div class="step-actions">
          <a-button type="primary" :disabled="fileList.length === 0" @click="nextStep">
            确定并下一步
          </a-button>
        </div>
      </div>

      <!-- 第二步：粘贴内容 -->
      <div v-if="currentStep === 1" class="step-content">
        <h2>粘贴合同内容</h2>
        <div class="paste-section">
          <a-textarea
            v-model:value="pasteContent"
            placeholder="请在此处粘贴合同内容..."
            :rows="10"
            show-count
            :maxlength="5000"
          />
        </div>
        <div class="step-actions">
          <a-button @click="prevStep">上一步</a-button>
          <a-button type="primary" :disabled="!pasteContent.trim()" @click="nextStep">
            确定并下一步
          </a-button>
        </div>
      </div>

      <!-- 第三步：等待处理 -->
      <div v-if="currentStep === 2" class="step-content">
        <h2>正在生成合同</h2>
        <div class="loading-section">
          <a-spin size="large" :spinning="true">
            <div class="loading-content">
              <LoadingOutlined style="font-size: 48px; color: #1890ff;" />
              <p style="margin-top: 16px;">正在智能生成合同，请稍候...</p>
              <p class="loading-tip">这通常需要几秒钟时间</p>
            </div>
          </a-spin>
        </div>
      </div>

      <!-- 第四步：完成 -->
      <div v-if="currentStep === 3" class="step-content">
        <h2>合同生成完成</h2>
        <div class="result-section">
          <div class="success-icon">
            <SmileOutlined style="font-size: 64px; color: #52c41a;" />
          </div>
          <p class="success-text">合同已成功生成！</p>
          <p class="success-desc">您可以选择下载合同文件或返回首页</p>
          
          <div class="result-actions">
            <a-button type="primary" size="large" @click="downloadContract">
              <template #icon>
                <DownloadOutlined />
              </template>
              下载合同
            </a-button>
            <a-button size="large" @click="goBack" style="margin-left: 16px;">
              返回首页
            </a-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { 
  LeftOutlined, 
  InboxOutlined, 
  LoadingOutlined, 
  SmileOutlined,
  DownloadOutlined,
  FileTextOutlined 
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'

const router = useRouter()

// 步骤数据
const currentStep = ref(0)
const fileList = ref([])
const pasteContent = ref('')

// 步骤配置
const steps = ref([
  {
    title: '上传文件',
    icon: h(InboxOutlined),
  },
  {
    title: '粘贴内容',
    icon: h(FileTextOutlined),
  },
  {
    title: '等待生成',
    icon: h(LoadingOutlined),
  },
  {
    title: '完成',
    icon: h(SmileOutlined),
  },
])

// 模拟后端处理定时器
let processingTimer = null

// 上传前验证
const beforeUpload = (file) => {
  const isAllowedType = file.type === 'application/pdf' || 
                       file.type === 'application/msword' || 
                       file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  if (!isAllowedType) {
    message.error('只能上传 PDF 或 Word 文档!')
    return false
  }
  
  const isLt10M = file.size / 1024 / 1024 < 10
  if (!isLt10M) {
    message.error('文件大小不能超过 10MB!')
    return false
  }
  
  // 手动添加到文件列表
  fileList.value = [...fileList.value, {
    uid: file.uid,
    name: file.name,
    status: 'done',
    url: '',
    originFileObj: file
  }]
  
  return false // 返回 false 阻止自动上传
}

// 自定义上传（实际项目中需要实现真正的上传逻辑）
const customUpload = () => {
  // 这里可以添加实际的上传逻辑
  console.log('开始上传文件...')
}

// 移除文件
const handleRemove = (file) => {
  const index = fileList.value.indexOf(file)
  const newFileList = fileList.value.slice()
  newFileList.splice(index, 1)
  fileList.value = newFileList
}

// 下一步
const nextStep = () => {
  if (currentStep.value === 2) {
    // 开始模拟后端处理
    startProcessing()
  } else {
    currentStep.value++
  }
}

// 上一步
const prevStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

// 开始处理
const startProcessing = () => {
  message.info('开始处理合同文件...')
  
  // 模拟后端处理时间（3-5秒）
  const processingTime = 3000 + Math.random() * 2000
  
  processingTimer = setTimeout(() => {
    currentStep.value = 3
    message.success('合同生成成功！')
  }, processingTime)
}

// 下载合同
const downloadContract = () => {
  message.success('开始下载合同文件')
  // 这里添加实际的下载逻辑
  // 模拟下载
  setTimeout(() => {
    message.success('下载完成！')
  }, 1000)
}

// 返回首页
const goBack = () => {
  router.push('/')
}

// 组件卸载时清理定时器
onUnmounted(() => {
  if (processingTimer) {
    clearTimeout(processingTimer)
  }
})

// 导入所需的图标组件
import { h } from 'vue'
</script>

<style scoped>
.fill-contract-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background-color: #fff;
  min-height: 100vh;
}

.back-button {
  margin-bottom: 30px;
}

.steps-container {
  margin-bottom: 40px;
}

.step-content {
  text-align: center;
  padding: 40px 20px;
}

.step-content h2 {
  margin-bottom: 30px;
  color: #333;
  font-size: 24px;
}

.upload-section,
.paste-section,
.loading-section,
.result-section {
  max-width: 600px;
  margin: 0 auto 30px;
}

.step-actions {
  margin-top: 30px;
}

.step-actions .ant-btn {
  margin: 0 8px;
}

.loading-content {
  padding: 40px 0;
}

.loading-tip {
  color: #999;
  font-size: 14px;
}

.success-icon {
  margin-bottom: 20px;
}

.success-text {
  font-size: 20px;
  color: #52c41a;
  margin-bottom: 8px;
}

.success-desc {
  color: #666;
  margin-bottom: 30px;
}

.result-actions {
  margin-top: 30px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .fill-contract-container {
    padding: 15px;
  }
  
  .step-content {
    padding: 20px 10px;
  }
  
  .step-content h2 {
    font-size: 20px;
  }
}
</style>